import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../team";

interface TimelineEvent {
  action?: string | null;
  appTitle?: string | null;
  attributeToApp?: string | null;
  createdAt?: string | null;
  message?: string | null;
}

export const LookupOrderTimelineCard = memo(function LookupOrderTimelineCard() {
  const client = useRequiredContext(RedoAdminRpcClientContext);
  const [orderNumber, setOrderNumber] = useState<string>("");

  const team = useRequiredContext(TeamContext);

  const [timelineLoad, triggerTimelineLoad] = useTriggerLoad(async () => {
    const result = await client.getOrderTimeline({
      orderNumber,
      teamId: team._id.toString(),
    });
    return result.timeline;
  });

  const handleLoadTimeline = () => {
    triggerTimelineLoad();
  };

  const renderTimelineItem = (item: TimelineEvent) => {
    return (
      <Flex
        borderColor="secondary"
        borderRadius="lg"
        borderStyle="solid"
        borderWidth="1px"
        dir="column"
        gap="sm"
        p="md"
      >
        {Object.entries(item).map(([key, value]) => (
          <Flex dir="row" gap="sm" key={key}>
            <Text fontSize="sm" fontWeight="medium" textColor="secondary">
              {key}:
            </Text>
            <Text fontSize="sm" textColor="primary">
              {value == null ? "N/A" : String(value)}
            </Text>
          </Flex>
        ))}
      </Flex>
    );
  };

  return (
    <Card
      subtitle="Enter an order number to load its timeline details"
      title="Lookup order timeline"
    >
      <Flex dir="column" gap="md">
        <Flex dir="row">
          <RedoTextInput
            fullWidth
            placeholder="#1001"
            setValue={setOrderNumber}
            value={orderNumber}
          />
          <RedoButton
            disabled={orderNumber.length === 0}
            hierarchy="primary"
            onClick={handleLoadTimeline}
            pending={timelineLoad.pending}
            text="Load timeline"
            theme="success"
          />
        </Flex>

        {timelineLoad.value && (
          <Flex dir="column" gap="md">
            <Text fontSize="md" fontWeight="semibold">
              Timeline for {orderNumber}
            </Text>
            {timelineLoad.value.map(renderTimelineItem)}
          </Flex>
        )}

        {timelineLoad.error && (
          <Text fontSize="sm" textColor="error">
            Error loading timeline: {timelineLoad.error.message}
          </Text>
        )}
      </Flex>
    </Card>
  );
});
